import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { accountService } from '../../services/accountService';
import toast from 'react-hot-toast';

const initialState = {
  accounts: [],
  selectedAccount: null,
  filters: {
    type: '',
    minPrice: '',
    maxPrice: '',
    search: '',
  },
  pagination: {
    page: 1,
    totalPages: 1,
    total: 0,
  },
  isLoading: false,
  error: null,
};

// Async thunks
export const fetchAccounts = createAsyncThunk(
  'accounts/fetchAccounts',
  async (params, { rejectWithValue }) => {
    try {
      const response = await accountService.getAccounts(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const fetchAdminAccounts = createAsyncThunk(
  'accounts/fetchAdminAccounts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await accountService.getAdminAccounts();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const fetchAccountById = createAsyncThunk(
  'accounts/fetchAccountById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await accountService.getAccountById(id);
      return response;
    } catch (error) {
      toast.error('Failed to fetch account details');
      return rejectWithValue(error.response?.data);
    }
  }
);

export const createAccount = createAsyncThunk(
  'accounts/createAccount',
  async (accountData, { rejectWithValue }) => {
    try {
      const response = await accountService.createAccount(accountData);
      toast.success('Account created successfully! ✨');
      return response;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create account');
      return rejectWithValue(error.response?.data);
    }
  }
);

export const updateAccount = createAsyncThunk(
  'accounts/updateAccount',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await accountService.updateAccount(id, data);
      toast.success('Account updated successfully! ✨');
      return response;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update account');
      return rejectWithValue(error.response?.data);
    }
  }
);

export const deleteAccount = createAsyncThunk(
  'accounts/deleteAccount',
  async (id, { rejectWithValue }) => {
    try {
      await accountService.deleteAccount(id);
      toast.success('Account deleted successfully');
      return id;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete account');
      return rejectWithValue(error.response?.data);
    }
  }
);

const accountSlice = createSlice({
  name: 'accounts',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    clearSelectedAccount: (state) => {
      state.selectedAccount = null;
    },
    setPage: (state, action) => {
      state.pagination.page = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Accounts
      .addCase(fetchAccounts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAccounts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.accounts = action.payload.accounts;
        state.pagination.total = action.payload.count;
        state.pagination.totalPages = Math.ceil(action.payload.count / 10);
      })
      .addCase(fetchAccounts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchAdminAccounts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAdminAccounts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.accounts = action.payload.accounts;
        state.pagination.total = action.payload.count;
        state.pagination.totalPages = Math.ceil(action.payload.count / 10);
      })
      .addCase(fetchAdminAccounts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch Single Account
      .addCase(fetchAccountById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAccountById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedAccount = action.payload.account;
      })
      .addCase(fetchAccountById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Create Account
      .addCase(createAccount.fulfilled, (state, action) => {
        state.accounts.unshift(action.payload.account);
      })
      // Update Account
      .addCase(updateAccount.fulfilled, (state, action) => {
        const index = state.accounts.findIndex(
          (a) => a._id === action.payload.account._id
        );
        if (index !== -1) {
          state.accounts[index] = action.payload.account;
        }
        if (state.selectedAccount?._id === action.payload.account._id) {
          state.selectedAccount = action.payload.account;
        }
      })
      // Delete Account
      .addCase(deleteAccount.fulfilled, (state, action) => {
        state.accounts = state.accounts.filter((a) => a._id !== action.payload);
        if (state.selectedAccount?._id === action.payload) {
          state.selectedAccount = null;
        }
      });
  },
});

export const { setFilters, clearFilters, clearSelectedAccount, setPage } = accountSlice.actions;
export default accountSlice.reducer;